import { useCallback, useEffect, useState } from "react";
import { IS_PREVIEW } from "./config";
import PreviewBar from "./components/PreviewBar";
import ui from "./components/ui.module.css";
import liveRepository from "./data/liveRepository";
import previewRepository from "./data/previewRepository";
import { OFFLINE, problemOf } from "./data/problems";
import useLearnerSession from "./hooks/useLearnerSession";
import Activity from "./screens/Activity";
import AssignmentDone from "./screens/AssignmentDone";
import Checking from "./screens/Checking";
import Complete from "./screens/Complete";
import MyLearning from "./screens/MyLearning";
import NotReady from "./screens/NotReady";
import Revision from "./screens/Revision";
import Setup, { setupCodeFrom } from "./screens/Setup";
import Waiting from "./screens/Waiting";
import YourTurn from "./screens/YourTurn";
import TeacherPreview, { PREVIEW_OPENED } from "./screens/TeacherPreview";

const repo = IS_PREVIEW ? previewRepository : liveRepository;

// A code scanned with the tablet's camera app opens this app with `?setup=`.
// It is taken out of the address straight away, so a refresh never offers the
// used code again.
const takeSetupCode = () => {
  const code = setupCodeFrom(window.location.href);
  const url = new URL(window.location.href);
  if (url.searchParams.has("setup")) {
    url.searchParams.delete("setup");
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  }
  return code;
};

// Read once, when the app loads, not during render.
const codeAtLaunch = IS_PREVIEW ? null : takeSetupCode();

// A teacher preview code works once. It is swapped for a marker so that
// reloading the preview explains itself instead of showing tablet setup.
const takeTeacherPreviewCode = () => {
  if (IS_PREVIEW) return null;
  const url = new URL(window.location.href);
  const code = url.searchParams.get("preview");
  if (code && code !== PREVIEW_OPENED) {
    url.searchParams.set("preview", PREVIEW_OPENED);
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  }
  return code;
};

const previewCodeAtLaunch = takeTeacherPreviewCode();

// A code can be redeemed only once, so each code is sent once however often
// the effect below runs (React runs it twice in development).
const redemptions = new Map();
const redeem = (code) => {
  if (!redemptions.has(code)) redemptions.set(code, repo.provision(code));
  return redemptions.get(code);
};

const setupProblem = (error) =>
  problemOf(error) === OFFLINE
    ? "This tablet is offline. Connect to the internet, then scan a new code."
    : error?.response?.status === 400
      ? "That code has already been used or has run out. Ask for a new one."
      : "The tablet couldn't be set up. Try again with a new code.";

const Screen = ({ session, setupError, onSetupCode }) => {
  switch (session.phase) {
    case "checking":
      return <Checking />;
    case "signed-out":
      return <Setup error={setupError} onCode={onSetupCode} />;
    case "not-ready":
      return (
        <NotReady
          reason={session.reason}
          onRecheck={session.recheck}
          onReset={IS_PREVIEW ? null : session.signOut}
        />
      );
    case "your-turn":
      return <YourTurn retry={session.retry} saving={session.saving} />;
    case "activity":
      return (
        <Activity
          session={session}
          // A live lesson has no way out; homework can be left and resumed.
          onBack={session.source?.kind === "assignment" ? session.goHome : undefined}
        />
      );
    case "complete":
      return <Complete />;
    case "assignment-done":
      return <AssignmentDone assignment={session.assignment} onDone={session.goHome} />;
    case "my-learning":
      return (
        <MyLearning
          items={session.items}
          saving={session.saving}
          retry={session.retry}
          onOpen={session.openRevision}
          onBack={session.goHome}
        />
      );
    case "revision":
      return (
        <Revision
          key={session.attempt?.id}
          attempt={session.attempt}
          activity={session.activity}
          title={session.title}
          onBack={session.openMyLearning}
        />
      );
    default:
      return (
        <Waiting
          assignments={session.assignments}
          saving={session.saving}
          retry={session.retry}
          onOpen={session.openAssignment}
          onMyLearning={session.openMyLearning}
          onReset={IS_PREVIEW ? null : session.signOut}
        />
      );
  }
};

const Session = ({ setupError, onSetupCode }) => {
  const session = useLearnerSession(repo);
  return (
    <>
      {IS_PREVIEW && <PreviewBar onChange={session.recheck} />}
      <Screen session={session} setupError={setupError} onSetupCode={onSetupCode} />
    </>
  );
};

const LearnerApp = () => {
  const [code, setCode] = useState(codeAtLaunch);
  const [setupError, setSetupError] = useState(null);

  // The session starts only once a code, if there is one, has been used, so it
  // begins from the tablet's new setup.
  useEffect(() => {
    if (!code) return undefined;
    let active = true;
    redeem(code).then(
      () => active && (setSetupError(null), setCode(null)),
      (error) => active && (setSetupError(setupProblem(error)), setCode(null))
    );
    return () => {
      active = false;
    };
  }, [code]);

  const onSetupCode = useCallback((scanned) => setCode(scanned), []);

  return (
    <div className={ui.app}>
      {code ? (
        <Checking />
      ) : (
        <Session setupError={setupError} onSetupCode={onSetupCode} />
      )}
    </div>
  );
};

const App = () => (
  previewCodeAtLaunch ? (
    <div className={ui.app}><TeacherPreview code={previewCodeAtLaunch} /></div>
  ) : <LearnerApp />
);

export default App;
